// Resolução da rota do front a partir da URL — é o que o gate de permissão
// consulta. Rota não encontrada significa NEGAR, e negar manda o operador para
// o login sem dizer por quê; por isso a resolução mora num lugar só, testável.
import type { FrontEndRoute } from "@/constants/front-end-routes"
import FRONTEND_ROUTES from "@/constants/front-end-routes"

type RouteName = keyof typeof FRONTEND_ROUTES

type ResolvedRoute = readonly [RouteName, FrontEndRoute]

const escapeRegExp = (segment: string) =>
  segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// `/a/:id` → /^\/a\/[^/]+(\/.*)?$/
const toPattern = (routePath: string) =>
  new RegExp(
    `^${routePath
      .split("/")
      .map((segment) =>
        segment.startsWith(":")
          ? "[^/]+"
          : escapeRegExp(segment)
      )
      .join("/")}(/.*)?$`,
    // O react-router casa sem diferenciar maiúsculas; o gate precisa casar igual.
    "i"
  )

// Mais longas primeiro: `/admin/processors` casaria `/admin/processors/GWP-1`
// por prefixo e responderia pelo detalhe, mascarando as permissões que a rota
// específica declara.
const CANDIDATES = (
  Object.entries(FRONTEND_ROUTES) as Array<
    [RouteName, FrontEndRoute]
  >
)
  .map(
    ([name, route]) =>
      [name, route, route.href.split("?")[0]] as const
  )
  .sort(([, , a], [, , b]) => b.length - a.length)

/**
 * O react-router ignora barra final e maiúsculas: `/payment-configurations/NEW/`
 * renderiza a criação. Sem normalizar, esse caminho escapava do literal e caía
 * no `:configId`, herdando a permissão de leitura.
 */
// O router decodifica a URL antes de casar: `/%6Eew` renderiza `/new`.
// URI malformada (`%E0%A4%A`) lança — aí fica a original, sem derrubar o gate.
const safeDecode = (path: string) => {
  try {
    return decodeURI(path)
  } catch {
    return path
  }
}

const normalizePath = (pathname: string) => {
  const path = safeDecode(
    pathname.split(/[?#]/)[0]
  ).toLowerCase()
  return path.length > 1
    ? path.replace(/\/+$/, "") || "/"
    : path
}

/**
 * A rota MAIS ESPECÍFICA que casa o pathname, entendendo `:param`.
 *
 * O match LITERAL vence o parametrizado: `/payment-configurations/:configId`
 * é mais longa que `/payment-configurations/new` e casaria `/new` primeiro —
 * a criação herdaria a permissão de leitura do detalhe.
 */
export const resolveFrontendRoute = (
  pathname: string
): ResolvedRoute | undefined => {
  const currentPath = normalizePath(pathname)

  const exact = CANDIDATES.find(
    ([, , routePath]) =>
      routePath.toLowerCase() === currentPath
  )
  if (exact) return [exact[0], exact[1]]

  const match = CANDIDATES.find(([, , routePath]) => {
    if (!routePath.includes(":")) {
      return currentPath.startsWith(
        `${routePath.toLowerCase()}/`
      )
    }
    return toPattern(routePath).test(currentPath)
  })
  return match ? [match[0], match[1]] : undefined
}
